import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sheet } from '../components/States'
import { useEnv } from '../context'
import {
  EVENT_KIND_LABEL,
  PARTICIPATION_LABEL,
  PARTICIPATION_ORDER,
  PAYMENT_LABEL,
  PAYMENT_ORDER,
  formatEventWhen,
  formatFee,
  splitEvents,
} from '../data/events'
import { useEvents, useInvalidateEvent, useSingleFlight, type ParticipantRowState } from '../data/hooks'
import { isoDate } from '../data/schedule'
import type { ClubEvent, EventParticipant, ParticipantPatch } from '../data/types'
import { useOnline } from '../platform/network'

/** Подпись состояния строки участника: сохраняется, сбой или итог с сервера. */
export function participantCaption(
  p: Pick<EventParticipant, 'status' | 'payment' | 'result'>,
  state: ParticipantRowState | undefined,
  online: boolean,
  mode: 'status' | 'fee',
): { text: string; tone: 'muted' | 'error' } {
  if (state?.phase === 'saving') return { text: 'Сохраняю…', tone: 'muted' }
  if (state?.phase === 'error') return { text: online ? 'Не сохранено' : 'Нет сети - не сохранено', tone: 'error' }
  if (mode === 'fee') return { text: PAYMENT_LABEL[p.payment], tone: 'muted' }
  return { text: [PARTICIPATION_LABEL[p.status], p.result].filter(Boolean).join(' / '), tone: 'muted' }
}

/**
 * Участник события: все статусы, взнос, результат, переход и «Убрать из
 * события» с подтверждением. Из карточки события шторка закрывается после
 * выбора (сохранение видно в строке), из карточки спортсмена - остаётся
 * открытой и показывает сохранение сама (keepOpen).
 */
export function ParticipantSheet({
  title,
  eventName,
  hasFee,
  participant: p,
  state,
  keepOpen,
  onSet,
  onRemove,
  link,
  onClose,
}: {
  title: string
  eventName: string
  hasFee: boolean
  participant: EventParticipant
  state?: ParticipantRowState
  keepOpen?: boolean
  onSet: (patch: ParticipantPatch) => void
  onRemove: () => Promise<boolean>
  link: { label: string; onClick: () => void }
  onClose: () => void
}) {
  const online = useOnline()
  const [result, setResult] = useState(p.result ?? '')
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [removeFailed, setRemoveFailed] = useState(false)
  const saving = state?.phase === 'saving'

  const choose = (patch: ParticipantPatch) => {
    onSet(patch)
    if (!keepOpen) onClose()
  }

  if (confirmRemove) {
    return (
      <Sheet title="Убрать из события?" onClose={() => setConfirmRemove(false)}>
        <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
          {p.name} пропадёт из участников «{eventName}» вместе с отметками участия и взноса по этому событию. Добавить
          снова можно в любой момент.
        </p>
        {removeFailed && (
          <div className="pc-alert" role="alert" style={{ marginBottom: 12 }}>
            {online ? 'Не получилось убрать. Попробуйте ещё раз.' : 'Нет сети - не сохранено.'}
          </div>
        )}
        <button
          type="button"
          className="pc-btn pc-btn--block pc-btn--danger"
          disabled={removing}
          onClick={async () => {
            setRemoving(true)
            setRemoveFailed(false)
            const ok = await onRemove()
            setRemoving(false)
            if (ok) onClose()
            else setRemoveFailed(true)
          }}
        >
          {removing ? 'Убираю…' : 'Убрать'}
        </button>
        <button type="button" className="pc-btn pc-btn--block" onClick={() => setConfirmRemove(false)}>
          Отмена
        </button>
      </Sheet>
    )
  }

  const caption = participantCaption(p, state, online, 'status')
  return (
    <Sheet title={title} onClose={onClose}>
      {keepOpen && (
        <p className="pc-roll-row__state" data-tone={caption.tone} role="status" style={{ margin: '0 0 8px' }}>
          {caption.text}
          {hasFee && !state && ` / взнос: ${PAYMENT_LABEL[p.payment].toLowerCase()}`}
        </p>
      )}
      <div className="pc-field__label" style={{ margin: '4px 0 8px' }}>
        Участие
      </div>
      <div role="group" aria-label="Участие" style={{ display: 'grid', gap: 8 }}>
        {PARTICIPATION_ORDER.map((s) => (
          <button
            key={s}
            type="button"
            className={`pc-btn pc-btn--block${p.status === s ? ' pc-btn--primary' : ''}`}
            aria-pressed={p.status === s}
            disabled={saving}
            onClick={() => choose({ status: s })}
          >
            {PARTICIPATION_LABEL[s]}
          </button>
        ))}
      </div>
      {hasFee && (
        <>
          <div className="pc-field__label" style={{ margin: '16px 0 8px' }}>
            Взнос
          </div>
          <div className="pc-segments" role="group" aria-label="Взнос">
            {PAYMENT_ORDER.map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={p.payment === s}
                disabled={saving}
                onClick={() => choose({ payment: s })}
                style={{ minHeight: 44 }}
              >
                {PAYMENT_LABEL[s]}
              </button>
            ))}
          </div>
        </>
      )}
      <form
        className="pc-field"
        style={{ marginTop: 16 }}
        onSubmit={(e) => {
          e.preventDefault()
          choose({ result })
        }}
      >
        <label htmlFor="pc-ev-result">Результат</label>
        <input
          id="pc-ev-result"
          className="pc-input"
          value={result}
          onChange={(e) => setResult(e.target.value)}
          placeholder="Например: 1 место, 2 кю"
          autoComplete="off"
        />
        <button type="submit" className="pc-btn" disabled={saving || result.trim() === (p.result ?? '')}>
          Сохранить результат
        </button>
      </form>
      <div style={{ display: 'grid', gap: 8, marginTop: 16 }}>
        <button type="button" className="pc-btn pc-btn--block" onClick={link.onClick}>
          {link.label}
        </button>
        <button type="button" className="pc-btn pc-btn--block pc-btn--danger" onClick={() => setConfirmRemove(true)}>
          Убрать из события
        </button>
      </div>
    </Sheet>
  )
}

/** Строка события в шторках выбора: название, тип и дата. */
function eventMeta(e: ClubEvent, thisYear: number) {
  return [EVENT_KIND_LABEL[e.kind], formatEventWhen(e, thisYear), e.fee ? `взнос ${formatFee(e.fee)}` : null]
    .filter(Boolean)
    .join(' / ')
}

/**
 * Выбор события: ближайшие, затем прошедшие (последние 5). Для «Записать
 * группу на событие» - дальше экран добавления с отмеченной группой.
 */
export function ChooseEventSheet({
  title,
  text,
  onChoose,
  onClose,
}: {
  title: string
  text?: string
  onChoose: (e: ClubEvent) => void
  onClose: () => void
}) {
  const { now } = useEnv()
  const navigate = useNavigate()
  const events = useEvents()
  const today = isoDate(now())
  const { upcoming, past } = splitEvents(events.data ?? [], today)

  const list = (items: ClubEvent[]) => (
    <ul className="pc-list" style={{ marginBottom: 8 }}>
      {items.map((e) => (
        <li key={e.id}>
          <button type="button" className="pc-list-row" onClick={() => onChoose(e)}>
            <span className="pc-list-row__main">
              <span className="pc-list-row__title" style={{ display: 'block' }}>
                {e.name}
              </span>
              <span className="pc-list-row__meta">{eventMeta(e, now().getFullYear())}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  )

  return (
    <Sheet title={title} onClose={onClose}>
      {text && (
        <p className="pc-muted" style={{ margin: '0 0 8px', fontSize: 14 }}>
          {text}
        </p>
      )}
      {events.isPending ? (
        <p className="pc-muted" role="status">
          Загружаю события…
        </p>
      ) : events.isError ? (
        <div className="pc-alert" role="alert">
          <span style={{ flex: 1 }}>Не удалось загрузить события</span>
          <button type="button" className="pc-btn" onClick={() => void events.refetch()}>
            Повторить
          </button>
        </div>
      ) : (
        <>
          {upcoming.length > 0 ? list(upcoming) : <p className="pc-muted">Ближайших событий нет.</p>}
          {past.length > 0 && (
            <>
              <div className="pc-field__label" style={{ marginTop: 8 }}>
                Прошедшие
              </div>
              {list(past.slice(0, 5))}
            </>
          )}
        </>
      )}
      <button type="button" className="pc-btn pc-btn--block" onClick={() => navigate('/schedule/events/new')}>
        Новое событие
      </button>
    </Sheet>
  )
}

/**
 * Один спортсмен в событие из его карточки: касание добавляет сразу
 * (одиночное и обратимое), в строке - «Добавлено» и «Отменить». Где уже
 * записан - «Уже в списке», недоступно.
 */
export function AddToEventSheet({
  athleteId,
  athleteName,
  joined,
  onClose,
}: {
  athleteId: string
  athleteName: string
  /** События, где спортсмен уже участвует. */
  joined: Set<string>
  onClose: () => void
}) {
  const { api, now } = useEnv()
  const online = useOnline()
  const events = useEvents()
  const invalidate = useInvalidateEvent()
  const flight = useSingleFlight()
  const [rows, setRows] = useState<Record<string, 'saving' | 'added' | 'error' | 'removed'>>({})
  const today = isoDate(now())
  const { upcoming, past } = splitEvents(events.data ?? [], today)

  // Одна запись за раз: пока идёт, другие касания ничего не шлют.
  const add = async (e: ClubEvent) => {
    if (flight.busy) return
    setRows((r) => ({ ...r, [e.id]: 'saving' }))
    try {
      const res = await flight.run(() => api.addEventParticipants(e.id, [athleteId]))
      if (res === null) return
      setRows((r) => ({ ...r, [e.id]: res.failed > 0 ? 'error' : 'added' }))
      await invalidate(e.id)
    } catch {
      setRows((r) => ({ ...r, [e.id]: 'error' }))
    }
  }

  const undo = async (e: ClubEvent) => {
    if (flight.busy) return
    setRows((r) => ({ ...r, [e.id]: 'saving' }))
    try {
      await flight.run(() => api.removeEventParticipant(e.id, athleteId))
      setRows((r) => ({ ...r, [e.id]: 'removed' }))
      await invalidate(e.id)
    } catch {
      setRows((r) => ({ ...r, [e.id]: 'added' }))
    }
  }

  const row = (e: ClubEvent) => {
    const st = rows[e.id]
    const already = joined.has(e.id) && st !== 'removed' && st !== 'added'
    let aside: ReactNode = 'Добавить'
    if (already) aside = 'Уже в списке'
    if (st === 'saving') aside = 'Сохраняю…'
    if (st === 'error') aside = online ? 'Не добавлено, повторить' : 'Нет сети - не сохранено'
    return (
      <li key={e.id}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            className="pc-list-row"
            disabled={already || st === 'saving' || st === 'added'}
            aria-label={already ? `${e.name}: уже в списке` : `Добавить в «${e.name}»`}
            onClick={() => void add(e)}
          >
            <span className="pc-list-row__main">
              <span className="pc-list-row__title" style={{ display: 'block' }}>
                {e.name}
              </span>
              <span className="pc-list-row__meta">{eventMeta(e, now().getFullYear())}</span>
            </span>
            {st !== 'added' && (
              <span className="pc-list-row__aside" style={st === 'error' ? { color: 'var(--pc-danger)' } : undefined}>
                {aside}
              </span>
            )}
          </button>
          {st === 'added' && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <span className="pc-roll-row__state" data-tone="ok" role="status">
                Добавлено
              </span>
              <button type="button" className="pc-btn" onClick={() => void undo(e)} aria-label={`Отменить добавление в «${e.name}»`}>
                Отменить
              </button>
            </span>
          )}
        </div>
      </li>
    )
  }

  return (
    <Sheet title={`Добавить в событие: ${athleteName}`} onClose={onClose}>
      {events.isPending ? (
        <p className="pc-muted" role="status">
          Загружаю события…
        </p>
      ) : events.isError ? (
        <div className="pc-alert" role="alert">
          <span style={{ flex: 1 }}>Не удалось загрузить события</span>
          <button type="button" className="pc-btn" onClick={() => void events.refetch()}>
            Повторить
          </button>
        </div>
      ) : upcoming.length + past.length === 0 ? (
        <p className="pc-muted">Событий пока нет. Их заводят в «Расписании» - «Соревнования и сборы».</p>
      ) : (
        <>
          {upcoming.length > 0 ? (
            <ul className="pc-list">{upcoming.map(row)}</ul>
          ) : (
            <p className="pc-muted">Ближайших событий нет.</p>
          )}
          {past.length > 0 && (
            <>
              <div className="pc-field__label" style={{ marginTop: 8 }}>
                Прошедшие
              </div>
              <ul className="pc-list">{past.slice(0, 5).map(row)}</ul>
            </>
          )}
        </>
      )}
      <button type="button" className="pc-btn pc-btn--block" style={{ marginTop: 8 }} onClick={onClose}>
        Готово
      </button>
    </Sheet>
  )
}
